export const RICH_TEXT_IMAGE_MAX_FILE_SIZE_BYTES = 8 * 1024 * 1024;
export const RICH_TEXT_IMAGE_MAX_DIMENSION = 1280;

const DEFAULT_IMAGE_ALT = 'workflow image';

const escapeHtmlAttribute = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const loadImageElement = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load image'));
    image.src = src;
  });

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      if (!result) {
        reject(new Error('Failed to read image file'));
        return;
      }
      resolve(result);
    };
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });

type ImageExportCandidate = {
  mimeType: string;
  quality?: number;
};

type ImageEmbedOptions = {
  maxLength?: number;
  maxDimension?: number;
};

const getImageExportCandidates = (file: File): ImageExportCandidate[] => {
  if ((file.type || '').toLowerCase() === 'image/png' && file.size <= 384 * 1024) {
    return [{ mimeType: 'image/png' }, { mimeType: 'image/jpeg', quality: 0.82 }];
  }

  return [
    { mimeType: 'image/jpeg', quality: 0.9 },
    { mimeType: 'image/jpeg', quality: 0.82 },
    { mimeType: 'image/jpeg', quality: 0.72 },
    { mimeType: 'image/jpeg', quality: 0.62 },
  ];
};

export const isSupportedRichTextImageType = (mimeType?: string): boolean =>
  (mimeType || '').trim().toLowerCase().startsWith('image/');

export const canInsertRichTextHtml = (
  currentHtml: string,
  htmlToInsert: string,
  maxLength?: number,
): boolean => {
  if (!maxLength || maxLength <= 0) {
    return true;
  }

  return (currentHtml || '').length + (htmlToInsert || '').length <= maxLength;
};

export const buildRichTextImageHtml = (src: string, alt = DEFAULT_IMAGE_ALT): string => {
  const safeSrc = escapeHtmlAttribute((src || '').trim());
  const safeAlt = escapeHtmlAttribute((alt || '').trim() || DEFAULT_IMAGE_ALT);
  return `<p><img src="${safeSrc}" alt="${safeAlt}" style="max-width:100%;height:auto;" /></p>`;
};

export const imageFileToEmbeddedHtml = async (
  file: File,
  options?: ImageEmbedOptions,
): Promise<string> => {
  if (!isSupportedRichTextImageType(file.type)) {
    throw new Error('Unsupported image format');
  }

  if (file.size > RICH_TEXT_IMAGE_MAX_FILE_SIZE_BYTES) {
    throw new Error('Image file is too large');
  }

  if (typeof document === 'undefined') {
    throw new Error('Image embedding is unavailable');
  }

  const maxDimension = options?.maxDimension || RICH_TEXT_IMAGE_MAX_DIMENSION;
  const source = await readFileAsDataUrl(file);
  const image = await loadImageElement(source);
  const scale = Math.min(1, maxDimension / Math.max(image.width || 1, image.height || 1));

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round((image.width || 1) * scale));
  canvas.height = Math.max(1, Math.round((image.height || 1) * scale));

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Image embedding is unavailable');
  }

  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  let shortestHtml = '';

  for (const candidate of getImageExportCandidates(file)) {
    const dataUrl = canvas.toDataURL(candidate.mimeType, candidate.quality);
    const html = buildRichTextImageHtml(dataUrl, file.name || DEFAULT_IMAGE_ALT);

    if (!shortestHtml || html.length < shortestHtml.length) {
      shortestHtml = html;
    }

    if (!options?.maxLength || html.length <= options.maxLength) {
      return html;
    }
  }

  if (shortestHtml && (!options?.maxLength || shortestHtml.length <= options.maxLength)) {
    return shortestHtml;
  }

  throw new Error('Image is too large to embed into the comment');
};
